import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { enviroment } from "../enviroment";
import { useProfileContext } from "./ProfileContext";
import { Role } from "../core/models/entities/Role";

interface PriceVisibilityContextProps {
  hidePrices: boolean;
  showCustomerPrices: boolean;
  togglePriceVisibility: () => void;
  toggleCustomerPrices: () => void;
}

const PriceVisibilityContext = createContext<
  PriceVisibilityContextProps | undefined
>(undefined);

interface PriceVisibilityProviderProps {
  children: ReactNode;
}

export function PriceVisibilityProvider({
  children,
}: PriceVisibilityProviderProps) {
  const [hidePrices, setHidePrices] = useState<boolean>(false);
  const [showCustomerPrices, setShowCustomerPrices] = useState<boolean>(false);
  const { role, organization, workshopMode } = useProfileContext();

  const togglePriceVisibility = () => {
    setHidePrices((prev) => !prev);
  };

  const toggleCustomerPrices = () => {
    if (role === Role.EndUser || workshopMode) return;
    setShowCustomerPrices((prev) => !prev);
  };

  const customerHasPricesHidden =
    role === Role.EndUser && organization?.hidePrices === true;

  // OR semantics: the per-user workshop flag always wins - the session
  // "Hide Prices" toggle can never undo it.
  const effectiveHidePrices =
    hidePrices || customerHasPricesHidden || workshopMode;

  // Flagged users are treated like end users: if a price ever leaks through
  // a missing hide-gate, it is the customer-facing price, never vendor cost.
  const effectiveShowCustomerPrices =
    role === Role.EndUser || workshopMode ? true : showCustomerPrices;

  // Admin perf FE-2 (flag adminPerfNavMemoEnabled): stable toggles + one
  // value object per (hidePrices, showCustomerPrices) pair. Flag off = the
  // literal object per render as before (hooks are called either way).
  const stableTogglePriceVisibility = useCallback(() => {
    setHidePrices((prev) => !prev);
  }, []);
  const stableToggleCustomerPrices = useCallback(() => {
    if (role === Role.EndUser || workshopMode) return;
    setShowCustomerPrices((prev) => !prev);
  }, [role, workshopMode]);
  const memoValue = useMemo(
    () => ({
      hidePrices: effectiveHidePrices,
      showCustomerPrices: effectiveShowCustomerPrices,
      togglePriceVisibility: stableTogglePriceVisibility,
      toggleCustomerPrices: stableToggleCustomerPrices,
    }),
    [
      effectiveHidePrices,
      effectiveShowCustomerPrices,
      stableTogglePriceVisibility,
      stableToggleCustomerPrices,
    ],
  );

  return (
    <PriceVisibilityContext.Provider
      value={
        enviroment.adminPerfNavMemoEnabled
          ? memoValue
          : {
              hidePrices: effectiveHidePrices,
              showCustomerPrices: effectiveShowCustomerPrices,
              togglePriceVisibility,
              toggleCustomerPrices,
            }
      }
    >
      {children}
    </PriceVisibilityContext.Provider>
  );
}

export function usePriceVisibility() {
  const context = useContext(PriceVisibilityContext);
  if (context === undefined) {
    throw new Error(
      "usePriceVisibility must be used within a PriceVisibilityProvider"
    );
  }
  return context;
}
